import React, { useState } from "react";
import {
  Users2,
  Globe,
  Scale,
  Heart,
  Megaphone,
  Palette,
  Plus,
  Calendar,
  User,
  Mail,
  Phone,
  Clock,
  CheckCircle,
  FileText,
  Eye,
  Edit,
  Trash2,
  Target,
  BarChart3,
  TrendingUp,
  Plane,
  MessageSquare,
} from "lucide-react";
import Card from "../components/ui/Card";
import Button from "../components/ui/Button";
import Input from "../components/ui/Input";
import Table from "../components/ui/Table";
import Modal from "../components/ui/Modal";
import Badge from "../components/ui/Badge";
import { format } from "date-fns";
import { useData } from "../context/DataContext";
import {
  Committee,
  Event,
  InternationalPartner,
  DisputeCase,
} from "../context/DataContext";
import { useToast } from "../context/ToastContext";

interface CommitteeEvent {
  id: string;
  title: string;
  committee: string;
  date: string;
  type: "meeting" | "event" | "workshop" | "conference";
  location: string;
  attendees: number;
  status: "planned" | "confirmed" | "completed" | "cancelled";
}

export default function Committees() {
  const { state, dispatch } = useData();
  const { showSuccess, showError } = useToast();
  const [activeTab, setActiveTab] = useState<
    "overview" | "international" | "dispute" | "social" | "media" | "cultural"
  >("overview");
  const [selectedCommittee, setSelectedCommittee] = useState<Committee | null>(
    null
  );
  const [showCommitteeModal, setShowCommitteeModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showEventModal, setShowEventModal] = useState(false);
  const [showPartnerModal, setShowPartnerModal] = useState(false);
  const [showDisputeModal, setShowDisputeModal] = useState(false);

  // Handler functions for CRUD operations
  const handleAddCommittee = (committeeData: Omit<Committee, "id">) => {
    try {
      const newCommittee: Committee = {
        ...committeeData,
        id: Date.now().toString(),
      };
      dispatch({ type: "ADD_COMMITTEE", payload: newCommittee });
      setShowAddModal(false);
      showSuccess(
        "Committee Added",
        `Committee "${newCommittee.name}" has been successfully added.`
      );
    } catch (error) {
      showError("Error", "Failed to add committee. Please try again.");
    }
  };

  const handleUpdateCommittee = (updatedCommittee: Committee) => {
    try {
      dispatch({ type: "UPDATE_COMMITTEE", payload: updatedCommittee });
      setShowEditModal(false);
      setSelectedCommittee(null);
      showSuccess(
        "Committee Updated",
        `Committee "${updatedCommittee.name}" has been successfully updated.`
      );
    } catch (error) {
      showError("Error", "Failed to update committee. Please try again.");
    }
  };

  const handleDeleteCommittee = (committeeId: string) => {
    const committee = state.committees.find((c) => c.id === committeeId);
    if (
      committee &&
      window.confirm(
        `Are you sure you want to delete the committee "${committee.name}"?`
      )
    ) {
      try {
        // Note: We need to add DELETE_COMMITTEE action to the reducer
        // For now, we'll filter it out manually
        const updatedCommittees = state.committees.filter(
          (c) => c.id !== committeeId
        );
        dispatch({ type: "SET_COMMITTEES", payload: updatedCommittees });
        showSuccess(
          "Committee Deleted",
          `Committee "${committee.name}" has been successfully deleted.`
        );
      } catch (error) {
        showError("Error", "Failed to delete committee. Please try again.");
      }
    }
  };

  const handleAddEvent = (eventData: Omit<Event, "id">) => {
    try {
      const newEvent: Event = {
        ...eventData,
        id: Date.now().toString(),
      };
      dispatch({ type: "ADD_EVENT", payload: newEvent });
      setShowEventModal(false);
      showSuccess(
        "Event Added",
        `Event "${newEvent.title}" has been successfully added.`
      );
    } catch (error) {
      showError("Error", "Failed to add event. Please try again.");
    }
  };

  const handleAddPartner = (partnerData: Omit<InternationalPartner, "id">) => {
    try {
      const newPartner: InternationalPartner = {
        ...partnerData,
        id: Date.now().toString(),
      };
      dispatch({ type: "ADD_INTERNATIONAL_PARTNER", payload: newPartner });
      setShowPartnerModal(false);
      showSuccess(
        "Partner Added",
        `International partner "${newPartner.name}" has been successfully added.`
      );
    } catch (error) {
      showError(
        "Error",
        "Failed to add international partner. Please try again."
      );
    }
  };

  const handleAddDisputeCase = (disputeData: Omit<DisputeCase, "id">) => {
    try {
      const newDispute: DisputeCase = {
        ...disputeData,
        id: Date.now().toString(),
      };
      dispatch({ type: "ADD_DISPUTE_CASE", payload: newDispute });
      setShowDisputeModal(false);
      showSuccess(
        "Dispute Case Added",
        `Dispute case "${newDispute.title}" has been successfully added.`
      );
    } catch (error) {
      showError("Error", "Failed to add dispute case. Please try again.");
    }
  };

  // Use data from context, fallback to sample data if empty
  const committees =
    state.committees.length > 0
      ? state.committees
      : [
          {
            id: "1",
            name: "International Relations Committee",
            type: "international" as const,
            description:
              "Manages international partnerships and cooperation with global aviation organizations",
            chairperson: "Captain Sarah Ahmed",
            members: ["1", "2", "3"],
            status: "active" as const,
            establishedDate: "2020-01-15",
            budget: 100000,
            objectives: [
              "Build international partnerships",
              "Represent syndicate globally",
            ],
          },
          {
            id: "2",
            name: "Dispute Resolution Committee",
            type: "dispute" as const,
            description:
              "Handles member disputes and provides mediation services",
            chairperson: "Captain Ahmed El-Sayed",
            members: ["2", "3"],
            status: "active" as const,
            establishedDate: "2019-06-20",
            budget: 75000,
            objectives: [
              "Resolve member disputes",
              "Provide mediation services",
            ],
          },
          {
            id: "3",
            name: "Social Committee",
            type: "social" as const,
            description: "Organizes social events and member activities",
            chairperson: "Captain Mariam Youssef",
            members: ["1", "3"],
            status: "active" as const,
            establishedDate: "2021-03-10",
            budget: 50000,
            objectives: ["Organize social events", "Build community"],
          },
          {
            id: "4",
            name: "Media Committee",
            type: "media" as const,
            description: "Manages public relations and media communications",
            chairperson: "Captain Rania Farid",
            members: ["1", "2"],
            status: "active" as const,
            establishedDate: "2020-09-05",
            budget: 60000,
            objectives: [
              "Manage public relations",
              "Handle media communications",
            ],
          },
          {
            id: "5",
            name: "Artistic & Cultural Committee",
            type: "artistic" as const,
            description:
              "Promotes cultural activities and artistic initiatives",
            chairperson: "Captain Layla Nasser",
            members: ["2", "3"],
            status: "active" as const,
            establishedDate: "2022-01-12",
            budget: 40000,
            objectives: [
              "Promote cultural activities",
              "Support artistic initiatives",
            ],
          },
        ];

  const committeeEvents: CommitteeEvent[] = [
    {
      id: "1",
      title: "ICAO Partnership Meeting",
      committee: "International Relations",
      date: "2024-02-15",
      type: "meeting",
      location: "Cairo Marriott Hotel",
      attendees: 2,
      status: "confirmed",
    },
    {
      id: "2",
      title: "Member Dispute Mediation",
      committee: "Dispute Resolution",
      date: "2024-02-10",
      type: "meeting",
      location: "Syndicate Office",
      attendees: 2,
      status: "planned",
    },
    {
      id: "3",
      title: "Annual Pilots Gala",
      committee: "Social",
      date: "2024-03-15",
      type: "event",
      location: "Grand Nile Tower",
      attendees: 2,
      status: "confirmed",
    },
    {
      id: "4",
      title: "Press Conference - Safety Initiative",
      committee: "Media",
      date: "2024-02-20",
      type: "conference",
      location: "Cairo International Convention Center",
      attendees: 2,
      status: "planned",
    },
    {
      id: "5",
      title: "Art Exhibition Opening",
      committee: "Artistic & Cultural",
      date: "2024-02-25",
      type: "event",
      location: "Cairo Opera House",
      attendees: 2,
      status: "confirmed",
    },
  ];

  // Sample data for international partners
  const internationalPartners = [
    {
      id: "1",
      name: "International Civil Aviation Organization (ICAO)",
      type: "International Organization",
      country: "Canada",
      contact: "Dr. Juan Carlos Salazar",
      email: "juan.salazar@icao.int",
      phone: "+1-514-954-8219",
      status: "Active",
      lastContact: "2024-01-10",
      notes: "Primary international aviation regulatory body",
    },
    {
      id: "2",
      name: "International Federation of Air Line Pilots' Associations (IFALPA)",
      type: "Pilot Association",
      country: "UK",
      contact: "Captain Jack Netskar",
      email: "jack.netskar@ifalpa.org",
      phone: "+44-20-7379-2611",
      status: "Active",
      lastContact: "2024-01-08",
      notes: "Global pilot association network",
    },
    {
      id: "3",
      name: "Egyptian Civil Aviation Authority (ECAA)",
      type: "National Authority",
      country: "Egypt",
      contact: "Captain Hossam Kamal",
      email: "hossam.kamal@ecaa.gov.eg",
      phone: "+20-2-2267-0000",
      status: "Active",
      lastContact: "2024-01-12",
      notes: "National aviation regulatory authority",
    },
  ];

  // Sample data for dispute cases
  const disputeCases = [
    {
      id: "1",
      title: "Pilot License Renewal Dispute",
      member: "Ahmed Hassan",
      type: "License Issue",
      status: "Under Review",
      submittedDate: "2024-01-05",
      assignedTo: "Captain Mohamed Ali",
      priority: "High",
      description: "Dispute regarding license renewal requirements",
    },
    {
      id: "2",
      title: "Employment Contract Dispute",
      member: "Sara Ahmed",
      type: "Employment",
      status: "Mediation",
      submittedDate: "2024-01-08",
      assignedTo: "Captain Youssef Ibrahim",
      priority: "Medium",
      description: "Dispute over employment contract terms",
    },
    {
      id: "3",
      title: "Training Program Dispute",
      member: "Omar Khaled",
      type: "Training",
      status: "Resolved",
      submittedDate: "2023-12-20",
      assignedTo: "Captain Fatma Mahmoud",
      priority: "Low",
      description: "Dispute over training program completion",
    },
  ];

  // Sample data for social events
  const socialEvents = [
    {
      id: "1",
      title: "Annual Pilot Gala",
      date: "2024-02-15",
      type: "Gala",
      status: "Planning",
      attendees: 200,
      budget: 50000,
      location: "Cairo Marriott Hotel",
      description: "Annual gala dinner for all pilots and families",
    },
    {
      id: "2",
      title: "Family Day Outing",
      date: "2024-03-20",
      type: "Family Event",
      status: "Confirmed",
      attendees: 150,
      budget: 15000,
      location: "Alexandria Corniche",
      description: "Family day at the beach with activities for children",
    },
    {
      id: "3",
      title: "Retirement Ceremony",
      date: "2024-01-30",
      type: "Ceremony",
      status: "Completed",
      attendees: 80,
      budget: 8000,
      location: "Syndicate Headquarters",
      description: "Ceremony honoring retiring pilots",
    },
  ];

  // Sample data for media campaigns
  const mediaCampaigns = [
    {
      id: "1",
      title: "Aviation Safety Awareness",
      type: "Safety Campaign",
      status: "Active",
      startDate: "2024-01-01",
      endDate: "2024-03-31",
      budget: 25000,
      reach: 50000,
      engagement: 1200,
      description: "Campaign to promote aviation safety awareness",
    },
    {
      id: "2",
      title: "Pilot Recruitment Drive",
      type: "Recruitment",
      status: "Planning",
      startDate: "2024-02-01",
      endDate: "2024-04-30",
      budget: 30000,
      reach: 75000,
      engagement: 0,
      description: "Campaign to attract new pilots to the profession",
    },
    {
      id: "3",
      title: "Union Benefits Promotion",
      type: "Benefits",
      status: "Completed",
      startDate: "2023-11-01",
      endDate: "2023-12-31",
      budget: 20000,
      reach: 40000,
      engagement: 800,
      description: "Campaign highlighting union benefits and services",
    },
  ];

  // Sample data for cultural programs
  const culturalPrograms = [
    {
      id: "1",
      title: "Aviation Photography Exhibition",
      type: "Exhibition",
      status: "Active",
      startDate: "2024-01-15",
      endDate: "2024-02-15",
      participants: 25,
      budget: 12000,
      location: "Cairo Opera House",
      description: "Exhibition showcasing aviation photography by members",
    },
    {
      id: "2",
      title: "Pilot Poetry Night",
      type: "Cultural Event",
      status: "Planning",
      startDate: "2024-03-10",
      endDate: "2024-03-10",
      participants: 40,
      budget: 5000,
      location: "Cultural Center",
      description: "Evening of poetry and literature by pilot members",
    },
    {
      id: "3",
      title: "Aviation History Book Launch",
      type: "Publication",
      status: "Completed",
      startDate: "2023-12-01",
      endDate: "2023-12-01",
      participants: 100,
      budget: 8000,
      location: "Book Fair",
      description: "Launch of book on Egyptian aviation history",
    },
  ];

  const getCommitteeIcon = (type: string) => {
    switch (type) {
      case "international":
        return Globe;
      case "dispute":
        return Scale;
      case "social":
        return Heart;
      case "media":
        return Megaphone;
      case "cultural":
        return Palette;
      default:
        return Users2;
    }
  };

  const getTypeBadge = (type: string) => {
    switch (type) {
      case "international":
        return <Badge variant="info">International</Badge>;
      case "dispute":
        return <Badge variant="warning">Dispute Resolution</Badge>;
      case "social":
        return <Badge variant="success">Social</Badge>;
      case "media":
        return <Badge variant="info">Media</Badge>;
      case "cultural":
        return <Badge variant="default">Cultural</Badge>;
      default:
        return <Badge variant="default">{type}</Badge>;
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "active":
        return <Badge variant="success">Active</Badge>;
      case "inactive":
        return <Badge variant="default">Inactive</Badge>;
      case "planned":
        return <Badge variant="info">Planned</Badge>;
      case "confirmed":
        return <Badge variant="success">Confirmed</Badge>;
      case "completed":
        return <Badge variant="default">Completed</Badge>;
      case "cancelled":
        return <Badge variant="error">Cancelled</Badge>;
      default:
        return <Badge variant="default">{status}</Badge>;
    }
  };

  const getEventTypeBadge = (type: string) => {
    switch (type) {
      case "meeting":
        return <Badge variant="info">Meeting</Badge>;
      case "event":
        return <Badge variant="success">Event</Badge>;
      case "workshop":
        return <Badge variant="warning">Workshop</Badge>;
      case "conference":
        return <Badge variant="info">Conference</Badge>;
      default:
        return <Badge variant="default">{type}</Badge>;
    }
  };

  const getPriorityBadge = (priority: string) => {
    switch (priority) {
      case "High":
        return <Badge variant="error">High</Badge>;
      case "Medium":
        return <Badge variant="warning">Medium</Badge>;
      case "Low":
        return <Badge variant="default">Low</Badge>;
      default:
        return <Badge variant="default">{priority}</Badge>;
    }
  };

  const committeeColumns = [
    {
      key: "name",
      header: "Committee",
      render: (committee: Committee) => (
        <div className="flex items-center">
          <div className="p-2 bg-primary-100 rounded-lg mr-3">
            {React.createElement(getCommitteeIcon(committee.type), {
              className: "h-5 w-5 text-primary-600",
            })}
          </div>
          <div>
            <div className="text-sm font-medium text-gray-900">
              {committee.name}
            </div>
            <div className="text-sm text-gray-500">{committee.chairperson}</div>
          </div>
        </div>
      ),
    },
    {
      key: "type",
      header: "Type",
      render: (committee: Committee) => getTypeBadge(committee.type),
    },
    {
      key: "members",
      header: "Members",
      render: (committee: Committee) => `${committee.members.length} members`,
    },
    {
      key: "status",
      header: "Status",
      render: (committee: Committee) => getStatusBadge(committee.status),
    },
    {
      key: "establishedDate",
      header: "Established",
      render: (committee: Committee) =>
        format(new Date(committee.establishedDate), "MMM dd, yyyy"),
    },
    {
      key: "members",
      header: "Members",
      render: (committee: Committee) => `${committee.members.length} members`,
    },
    {
      key: "actions",
      header: "Actions",
      render: (committee: Committee) => (
        <div className="flex space-x-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setSelectedCommittee(committee);
              setShowCommitteeModal(true);
            }}
            title="View Details"
          >
            <Eye className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setSelectedCommittee(committee);
              setShowEditModal(true);
            }}
            title="Edit Committee"
          >
            <Edit className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => handleDeleteCommittee(committee.id)}
            title="Delete Committee"
            className="text-red-600 hover:text-red-800"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      ),
    },
  ];

  const eventColumns = [
    {
      key: "title",
      header: "Event",
      render: (event: CommitteeEvent) => (
        <div>
          <div className="text-sm font-medium text-gray-900">{event.title}</div>
          <div className="text-sm text-gray-500">{event.committee}</div>
        </div>
      ),
    },
    {
      key: "type",
      header: "Type",
      render: (event: CommitteeEvent) => getEventTypeBadge(event.type),
    },
    {
      key: "date",
      header: "Date",
      render: (event: CommitteeEvent) =>
        format(new Date(event.date), "MMM dd, yyyy"),
    },
    {
      key: "location",
      header: "Location",
      render: (event: CommitteeEvent) => event.location,
    },
    {
      key: "attendees",
      header: "Attendees",
      render: (event: CommitteeEvent) => `${event.attendees} people`,
    },
    {
      key: "status",
      header: "Status",
      render: (event: CommitteeEvent) => getStatusBadge(event.status),
    },
    {
      key: "actions",
      header: "Actions",
      render: (event: CommitteeEvent) => (
        <div className="flex space-x-2">
          <Button variant="ghost" size="sm">
            <Eye className="h-4 w-4" />
          </Button>
          <Button variant="ghost" size="sm">
            <Edit className="h-4 w-4" />
          </Button>
        </div>
      ),
    },
  ];

  // Table columns for international partners
  const partnerColumns = [
    {
      key: "name",
      header: "Organization",
      render: (partner: any) => (
        <div>
          <div className="font-medium">{partner.name}</div>
          <div className="text-sm text-gray-500">{partner.type}</div>
        </div>
      ),
    },
    {
      key: "country",
      header: "Country",
      render: (partner: any) => (
        <div className="flex items-center space-x-2">
          <Globe className="h-4 w-4 text-gray-400" />
          <span>{partner.country}</span>
        </div>
      ),
    },
    {
      key: "contact",
      header: "Contact",
      render: (partner: any) => (
        <div>
          <div className="font-medium">{partner.contact}</div>
          <div className="text-sm text-gray-500">{partner.email}</div>
        </div>
      ),
    },
    {
      key: "status",
      header: "Status",
      render: (partner: any) => getStatusBadge(partner.status),
    },
    {
      key: "lastContact",
      header: "Last Contact",
      render: (partner: any) => (
        <div className="text-sm">
          {new Date(partner.lastContact).toLocaleDateString()}
        </div>
      ),
    },
    {
      key: "actions",
      header: "Actions",
      render: (partner: any) => (
        <div className="flex space-x-2">
          <Button variant="outline" size="sm">
            <Mail className="h-4 w-4" />
          </Button>
          <Button variant="outline" size="sm">
            <Phone className="h-4 w-4" />
          </Button>
          <Button variant="outline" size="sm">
            <Edit className="h-4 w-4" />
          </Button>
        </div>
      ),
    },
  ];

  // Table columns for dispute cases
  const disputeColumns = [
    {
      key: "title",
      header: "Case",
      render: (case_: any) => (
        <div>
          <div className="font-medium">{case_.title}</div>
          <div className="text-sm text-gray-500">{case_.member}</div>
        </div>
      ),
    },
    {
      key: "type",
      header: "Type",
      render: (case_: any) => <Badge variant="default">{case_.type}</Badge>,
    },
    {
      key: "status",
      header: "Status",
      render: (case_: any) => getStatusBadge(case_.status),
    },
    {
      key: "priority",
      header: "Priority",
      render: (case_: any) => getPriorityBadge(case_.priority),
    },
    {
      key: "assignedTo",
      header: "Assigned To",
      render: (case_: any) => <div className="text-sm">{case_.assignedTo}</div>,
    },
    {
      key: "submittedDate",
      header: "Submitted",
      render: (case_: any) => (
        <div className="text-sm">
          {new Date(case_.submittedDate).toLocaleDateString()}
        </div>
      ),
    },
    {
      key: "actions",
      header: "Actions",
      render: (case_: any) => (
        <div className="flex space-x-2">
          <Button variant="outline" size="sm">
            <Eye className="h-4 w-4" />
          </Button>
          <Button variant="outline" size="sm">
            <Edit className="h-4 w-4" />
          </Button>
        </div>
      ),
    },
  ];

  const filteredCommittees = committees.filter((committee) => {
    if (activeTab === "overview") return true;
    return committee.type === activeTab;
  });

  const filteredEvents = committeeEvents.filter((event) => {
    if (activeTab === "overview") return true;
    const committee = committees.find((c) => c.name === event.committee);
    return committee?.type === activeTab;
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            Committee Management
          </h1>
          <p className="text-gray-600">
            Manage committees and their activities
          </p>
        </div>
        <Button onClick={() => setShowAddModal(true)}>
          <Plus className="h-4 w-4 mr-2" />
          Add Committee
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
        {committees.map((committee) => {
          const Icon = getCommitteeIcon(committee.type);
          return (
            <Card
              key={committee.id}
              className="cursor-pointer hover:shadow-md transition-shadow"
            >
              <div className="flex items-center">
                <div className="p-3 bg-primary-100 rounded-lg">
                  <Icon className="h-6 w-6 text-primary-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">
                    {committee.name}
                  </p>
                  <p className="text-2xl font-bold text-gray-900">
                    {committee.members.length}
                  </p>
                  <p className="text-xs text-gray-500">members</p>
                </div>
              </div>
            </Card>
          );
        })}
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {[
            { id: "overview", name: "Overview", icon: Users2 },
            {
              id: "international",
              name: "International Relations",
              icon: Globe,
            },
            { id: "dispute", name: "Dispute Resolution", icon: Scale },
            { id: "social", name: "Social", icon: Heart },
            { id: "media", name: "Media", icon: Megaphone },
            { id: "cultural", name: "Cultural", icon: Palette },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as any)}
              className={`flex items-center py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === tab.id
                  ? "border-primary-500 text-primary-600"
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
              }`}
            >
              <tab.icon className="h-4 w-4 mr-2" />
              {tab.name}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === "overview" && (
        <div className="space-y-6">
          {/* Recent Events */}
          <Card>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Recent Committee Events
            </h3>
            <div className="space-y-4">
              {committeeEvents.slice(0, 5).map((event) => (
                <div
                  key={event.id}
                  className="flex items-center justify-between p-4 bg-gray-50 rounded-lg"
                >
                  <div className="flex items-center space-x-4">
                    <div className="p-2 bg-primary-100 rounded-lg">
                      <Calendar className="h-4 w-4 text-primary-600" />
                    </div>
                    <div>
                      <h4 className="text-sm font-medium text-gray-900">
                        {event.title}
                      </h4>
                      <p className="text-sm text-gray-500">
                        {event.committee} • {event.location}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3">
                    {getEventTypeBadge(event.type)}
                    {getStatusBadge(event.status)}
                    <span className="text-sm text-gray-500">
                      {format(new Date(event.date), "MMM dd")}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </Card>

          {/* Committees Overview */}
          <Card padding="none">
            <Table
              data={filteredCommittees}
              columns={committeeColumns}
              onRowClick={(committee) => {
                setSelectedCommittee(committee);
                setShowCommitteeModal(true);
              }}
              emptyMessage="No committees found"
            />
          </Card>
        </div>
      )}

      {/* Individual Committee Tabs */}
      {activeTab !== "overview" && (
        <div className="space-y-6">
          {/* Committee Info */}
          <Card>
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <div className="p-3 bg-primary-100 rounded-lg">
                  {React.createElement(getCommitteeIcon(activeTab), {
                    className: "h-6 w-6 text-primary-600",
                  })}
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    {committees.find((c) => c.type === activeTab)?.name}
                  </h3>
                  <p className="text-gray-600">
                    {committees.find((c) => c.type === activeTab)?.description}
                  </p>
                </div>
              </div>
              <Button onClick={() => setShowEventModal(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Add Event
              </Button>
            </div>
          </Card>

          {/* Committee Members */}
          <Card>
            <h4 className="font-semibold text-gray-900 mb-4">
              Committee Members
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {committees
                .find((c) => c.type === activeTab)
                ?.members.map((member, index) => (
                  <div
                    key={index}
                    className="flex items-center p-3 bg-gray-50 rounded-lg"
                  >
                    <div className="h-10 w-10 rounded-full bg-primary-100 flex items-center justify-center">
                      <User className="h-5 w-5 text-primary-600" />
                    </div>
                    <div className="ml-3">
                      <div className="text-sm font-medium text-gray-900">
                        {member}
                      </div>
                      <div className="text-sm text-gray-500">
                        Committee Member
                      </div>
                    </div>
                  </div>
                ))}
            </div>
          </Card>

          {/* Committee-specific content */}
          {activeTab === "international" && (
            <>
              {/* International Partners */}
              <Card>
                <div className="flex items-center justify-between mb-4">
                  <h4 className="font-semibold text-gray-900">
                    International Partners
                  </h4>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowPartnerModal(true)}
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Add Partner
                  </Button>
                </div>
                <Card padding="none">
                  <Table
                    data={internationalPartners}
                    columns={partnerColumns}
                    emptyMessage="No international partners found"
                  />
                </Card>
              </Card>

              {/* Partnership Statistics */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <Card>
                  <div className="flex items-center space-x-3">
                    <div className="p-2 bg-blue-100 rounded-lg">
                      <Globe className="h-5 w-5 text-blue-600" />
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-gray-900">
                        {internationalPartners.length}
                      </div>
                      <div className="text-sm text-gray-500">
                        Active Partners
                      </div>
                    </div>
                  </div>
                </Card>
                <Card>
                  <div className="flex items-center space-x-3">
                    <div className="p-2 bg-green-100 rounded-lg">
                      <Users2 className="h-5 w-5 text-green-600" />
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-gray-900">12</div>
                      <div className="text-sm text-gray-500">
                        Active Agreements
                      </div>
                    </div>
                  </div>
                </Card>
                <Card>
                  <div className="flex items-center space-x-3">
                    <div className="p-2 bg-purple-100 rounded-lg">
                      <Plane className="h-5 w-5 text-purple-600" />
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-gray-900">8</div>
                      <div className="text-sm text-gray-500">
                        Upcoming Visits
                      </div>
                    </div>
                  </div>
                </Card>
              </div>
            </>
          )}

          {activeTab === "dispute" && (
            <>
              {/* Dispute Cases */}
              <Card>
                <div className="flex items-center justify-between mb-4">
                  <h4 className="font-semibold text-gray-900">
                    Active Dispute Cases
                  </h4>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowDisputeModal(true)}
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    New Case
                  </Button>
                </div>
                <Card padding="none">
                  <Table
                    data={disputeCases}
                    columns={disputeColumns}
                    emptyMessage="No dispute cases found"
                  />
                </Card>
              </Card>

              {/* Dispute Statistics */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                <Card>
                  <div className="flex items-center space-x-3">
                    <div className="p-2 bg-red-100 rounded-lg">
                      <Scale className="h-5 w-5 text-red-600" />
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-gray-900">
                        {disputeCases.length}
                      </div>
                      <div className="text-sm text-gray-500">Total Cases</div>
                    </div>
                  </div>
                </Card>
                <Card>
                  <div className="flex items-center space-x-3">
                    <div className="p-2 bg-yellow-100 rounded-lg">
                      <Clock className="h-5 w-5 text-yellow-600" />
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-gray-900">2</div>
                      <div className="text-sm text-gray-500">Under Review</div>
                    </div>
                  </div>
                </Card>
                <Card>
                  <div className="flex items-center space-x-3">
                    <div className="p-2 bg-blue-100 rounded-lg">
                      <MessageSquare className="h-5 w-5 text-blue-600" />
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-gray-900">1</div>
                      <div className="text-sm text-gray-500">In Mediation</div>
                    </div>
                  </div>
                </Card>
                <Card>
                  <div className="flex items-center space-x-3">
                    <div className="p-2 bg-green-100 rounded-lg">
                      <CheckCircle className="h-5 w-5 text-green-600" />
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-gray-900">1</div>
                      <div className="text-sm text-gray-500">Resolved</div>
                    </div>
                  </div>
                </Card>
              </div>
            </>
          )}

          {activeTab === "social" && (
            <>
              {/* Social Events */}
              <Card>
                <div className="flex items-center justify-between mb-4">
                  <h4 className="font-semibold text-gray-900">Social Events</h4>
                  <Button variant="outline" size="sm">
                    <Plus className="h-4 w-4 mr-2" />
                    New Event
                  </Button>
                </div>
                <div className="space-y-4">
                  {socialEvents.map((event) => (
                    <div
                      key={event.id}
                      className="flex items-center justify-between p-4 border rounded-lg"
                    >
                      <div className="flex items-center space-x-4">
                        <div className="p-2 bg-pink-100 rounded-lg">
                          <Heart className="h-5 w-5 text-pink-600" />
                        </div>
                        <div>
                          <div className="font-medium text-gray-900">
                            {event.title}
                          </div>
                          <div className="text-sm text-gray-500">
                            {event.location} • {event.attendees} attendees
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center space-x-3">
                        {getStatusBadge(event.status)}
                        <div className="text-sm text-gray-500">
                          {new Date(event.date).toLocaleDateString()}
                        </div>
                        <div className="text-sm font-medium text-gray-900">
                          EGP {event.budget.toLocaleString()}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </Card>

              {/* Social Statistics */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <Card>
                  <div className="flex items-center space-x-3">
                    <div className="p-2 bg-pink-100 rounded-lg">
                      <Heart className="h-5 w-5 text-pink-600" />
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-gray-900">
                        {socialEvents.length}
                      </div>
                      <div className="text-sm text-gray-500">Total Events</div>
                    </div>
                  </div>
                </Card>
                <Card>
                  <div className="flex items-center space-x-3">
                    <div className="p-2 bg-green-100 rounded-lg">
                      <Users2 className="h-5 w-5 text-green-600" />
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-gray-900">
                        430
                      </div>
                      <div className="text-sm text-gray-500">
                        Total Attendees
                      </div>
                    </div>
                  </div>
                </Card>
                <Card>
                  <div className="flex items-center space-x-3">
                    <div className="p-2 bg-blue-100 rounded-lg">
                      <Target className="h-5 w-5 text-blue-600" />
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-gray-900">
                        EGP{" "}
                        {socialEvents
                          .reduce((sum, event) => sum + event.budget, 0)
                          .toLocaleString()}
                      </div>
                      <div className="text-sm text-gray-500">Total Budget</div>
                    </div>
                  </div>
                </Card>
              </div>
            </>
          )}

          {activeTab === "media" && (
            <>
              {/* Media Campaigns */}
              <Card>
                <div className="flex items-center justify-between mb-4">
                  <h4 className="font-semibold text-gray-900">
                    Media Campaigns
                  </h4>
                  <Button variant="outline" size="sm">
                    <Plus className="h-4 w-4 mr-2" />
                    New Campaign
                  </Button>
                </div>
                <div className="space-y-4">
                  {mediaCampaigns.map((campaign) => (
                    <div
                      key={campaign.id}
                      className="flex items-center justify-between p-4 border rounded-lg"
                    >
                      <div className="flex items-center space-x-4">
                        <div className="p-2 bg-blue-100 rounded-lg">
                          <Megaphone className="h-5 w-5 text-blue-600" />
                        </div>
                        <div>
                          <div className="font-medium text-gray-900">
                            {campaign.title}
                          </div>
                          <div className="text-sm text-gray-500">
                            {campaign.type} • {campaign.reach.toLocaleString()}{" "}
                            reach
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center space-x-3">
                        {getStatusBadge(campaign.status)}
                        <div className="text-sm text-gray-500">
                          {new Date(campaign.startDate).toLocaleDateString()} -{" "}
                          {new Date(campaign.endDate).toLocaleDateString()}
                        </div>
                        <div className="text-sm font-medium text-gray-900">
                          EGP {campaign.budget.toLocaleString()}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </Card>

              {/* Media Statistics */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                <Card>
                  <div className="flex items-center space-x-3">
                    <div className="p-2 bg-blue-100 rounded-lg">
                      <Megaphone className="h-5 w-5 text-blue-600" />
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-gray-900">
                        {mediaCampaigns.length}
                      </div>
                      <div className="text-sm text-gray-500">
                        Total Campaigns
                      </div>
                    </div>
                  </div>
                </Card>
                <Card>
                  <div className="flex items-center space-x-3">
                    <div className="p-2 bg-green-100 rounded-lg">
                      <TrendingUp className="h-5 w-5 text-green-600" />
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-gray-900">
                        {mediaCampaigns
                          .reduce((sum, campaign) => sum + campaign.reach, 0)
                          .toLocaleString()}
                      </div>
                      <div className="text-sm text-gray-500">Total Reach</div>
                    </div>
                  </div>
                </Card>
                <Card>
                  <div className="flex items-center space-x-3">
                    <div className="p-2 bg-purple-100 rounded-lg">
                      <BarChart3 className="h-5 w-5 text-purple-600" />
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-gray-900">
                        {mediaCampaigns.reduce(
                          (sum, campaign) => sum + campaign.engagement,
                          0
                        )}
                      </div>
                      <div className="text-sm text-gray-500">
                        Total Engagement
                      </div>
                    </div>
                  </div>
                </Card>
                <Card>
                  <div className="flex items-center space-x-3">
                    <div className="p-2 bg-yellow-100 rounded-lg">
                      <Target className="h-5 w-5 text-yellow-600" />
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-gray-900">
                        EGP{" "}
                        {mediaCampaigns
                          .reduce((sum, campaign) => sum + campaign.budget, 0)
                          .toLocaleString()}
                      </div>
                      <div className="text-sm text-gray-500">Total Budget</div>
                    </div>
                  </div>
                </Card>
              </div>
            </>
          )}

          {activeTab === "cultural" && (
            <>
              {/* Cultural Programs */}
              <Card>
                <div className="flex items-center justify-between mb-4">
                  <h4 className="font-semibold text-gray-900">
                    Cultural Programs
                  </h4>
                  <Button variant="outline" size="sm">
                    <Plus className="h-4 w-4 mr-2" />
                    New Program
                  </Button>
                </div>
                <div className="space-y-4">
                  {culturalPrograms.map((program) => (
                    <div
                      key={program.id}
                      className="flex items-center justify-between p-4 border rounded-lg"
                    >
                      <div className="flex items-center space-x-4">
                        <div className="p-2 bg-purple-100 rounded-lg">
                          <Palette className="h-5 w-5 text-purple-600" />
                        </div>
                        <div>
                          <div className="font-medium text-gray-900">
                            {program.title}
                          </div>
                          <div className="text-sm text-gray-500">
                            {program.type} • {program.participants} participants
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center space-x-3">
                        {getStatusBadge(program.status)}
                        <div className="text-sm text-gray-500">
                          {new Date(program.startDate).toLocaleDateString()} -{" "}
                          {new Date(program.endDate).toLocaleDateString()}
                        </div>
                        <div className="text-sm font-medium text-gray-900">
                          EGP {program.budget.toLocaleString()}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </Card>

              {/* Cultural Statistics */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <Card>
                  <div className="flex items-center space-x-3">
                    <div className="p-2 bg-purple-100 rounded-lg">
                      <Palette className="h-5 w-5 text-purple-600" />
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-gray-900">
                        {culturalPrograms.length}
                      </div>
                      <div className="text-sm text-gray-500">
                        Total Programs
                      </div>
                    </div>
                  </div>
                </Card>
                <Card>
                  <div className="flex items-center space-x-3">
                    <div className="p-2 bg-green-100 rounded-lg">
                      <Users2 className="h-5 w-5 text-green-600" />
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-gray-900">
                        {culturalPrograms.reduce(
                          (sum, program) => sum + program.participants,
                          0
                        )}
                      </div>
                      <div className="text-sm text-gray-500">
                        Total Participants
                      </div>
                    </div>
                  </div>
                </Card>
                <Card>
                  <div className="flex items-center space-x-3">
                    <div className="p-2 bg-blue-100 rounded-lg">
                      <Target className="h-5 w-5 text-blue-600" />
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-gray-900">
                        EGP{" "}
                        {culturalPrograms
                          .reduce((sum, program) => sum + program.budget, 0)
                          .toLocaleString()}
                      </div>
                      <div className="text-sm text-gray-500">Total Budget</div>
                    </div>
                  </div>
                </Card>
              </div>
            </>
          )}

          {/* Committee Events */}
          <Card>
            <h4 className="font-semibold text-gray-900 mb-4">
              Upcoming Events
            </h4>
            <Card padding="none">
              <Table
                data={filteredEvents}
                columns={eventColumns}
                emptyMessage="No events scheduled"
              />
            </Card>
          </Card>
        </div>
      )}

      {/* Committee Details Modal */}
      {selectedCommittee && (
        <CommitteeDetailsModal
          committee={selectedCommittee}
          isOpen={showCommitteeModal}
          onClose={() => {
            setShowCommitteeModal(false);
            setSelectedCommittee(null);
          }}
        />
      )}

      {/* Add Committee Modal */}
      <AddCommitteeModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onAdd={handleAddCommittee}
      />

      {/* Edit Committee Modal */}
      {selectedCommittee && (
        <EditCommitteeModal
          committee={selectedCommittee}
          isOpen={showEditModal}
          onClose={() => {
            setShowEditModal(false);
            setSelectedCommittee(null);
          }}
          onUpdate={handleUpdateCommittee}
        />
      )}

      {/* Add Event Modal */}
      <AddEventModal
        isOpen={showEventModal}
        onClose={() => setShowEventModal(false)}
        onAdd={handleAddEvent}
        committeeType={activeTab}
      />

      {/* Add Partner Modal */}
      <AddPartnerModal
        isOpen={showPartnerModal}
        onClose={() => setShowPartnerModal(false)}
        onAdd={handleAddPartner}
      />

      {/* Add Dispute Case Modal */}
      <AddDisputeCaseModal
        isOpen={showDisputeModal}
        onClose={() => setShowDisputeModal(false)}
        onAdd={handleAddDisputeCase}
      />
    </div>
  );
}

// Committee Details Modal Component
function CommitteeDetailsModal({
  committee,
  isOpen,
  onClose,
}: {
  committee: Committee;
  isOpen: boolean;
  onClose: () => void;
}) {
  const { showInfo } = useToast();
  const Icon =
    committee.type === "international"
      ? Globe
      : committee.type === "dispute"
      ? Scale
      : committee.type === "social"
      ? Heart
      : committee.type === "media"
      ? Megaphone
      : committee.type === "artistic"
      ? Palette
      : Users2;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Committee Details"
      size="lg"
    >
      <div className="space-y-6">
        {/* Committee Header */}
        <div className="flex items-center space-x-4">
          <div className="p-4 bg-primary-100 rounded-lg">
            <Icon className="h-8 w-8 text-primary-600" />
          </div>
          <div>
            <h3 className="text-xl font-bold text-gray-900">
              {committee.name}
            </h3>
            <p className="text-gray-600">{committee.description}</p>
          </div>
        </div>

        {/* Committee Information */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <h4 className="font-semibold text-gray-900">
              Committee Information
            </h4>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Chairperson
                </label>
                <p className="text-sm text-gray-900">{committee.chairperson}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Established Date
                </label>
                <p className="text-sm text-gray-900">
                  {format(new Date(committee.establishedDate), "MMM dd, yyyy")}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Budget
                </label>
                <p className="text-sm text-gray-900">
                  EGP {committee.budget?.toLocaleString() || "0"}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Status
                </label>
                <div className="mt-1">
                  {committee.status === "active" ? (
                    <Badge variant="success">Active</Badge>
                  ) : (
                    <Badge variant="default">Inactive</Badge>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <h4 className="font-semibold text-gray-900">Committee Members</h4>
            <div className="space-y-3">
              {committee.members.map((member, index) => (
                <div
                  key={index}
                  className="flex items-center p-3 bg-gray-50 rounded-lg"
                >
                  <div className="h-8 w-8 rounded-full bg-primary-100 flex items-center justify-center">
                    <User className="h-4 w-4 text-primary-600" />
                  </div>
                  <div className="ml-3">
                    <div className="text-sm font-medium text-gray-900">
                      {member}
                    </div>
                    <div className="text-sm text-gray-500">
                      Committee Member
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Button
            variant="outline"
            className="flex flex-col items-center p-4"
            onClick={() => {
              onClose();
              // This would trigger the schedule meeting modal in the parent component
              // For now, we'll show an alert
              showInfo(
                "Schedule Meeting",
                `Schedule meeting functionality for ${committee.name} would be implemented here.`
              );
            }}
          >
            <Calendar className="h-6 w-6 mb-2" />
            <span className="text-sm">Schedule Meeting</span>
          </Button>
          <Button
            variant="outline"
            className="flex flex-col items-center p-4"
            onClick={() => {
              onClose();
              // This would trigger the send message modal
              showInfo(
                "Send Message",
                `Send message functionality to ${committee.name} members would be implemented here.`
              );
            }}
          >
            <Mail className="h-6 w-6 mb-2" />
            <span className="text-sm">Send Message</span>
          </Button>
          <Button
            variant="outline"
            className="flex flex-col items-center p-4"
            onClick={() => {
              onClose();
              // This would show committee reports
              showInfo(
                "View Reports",
                `View reports functionality for ${committee.name} would be implemented here.`
              );
            }}
          >
            <FileText className="h-6 w-6 mb-2" />
            <span className="text-sm">View Reports</span>
          </Button>
          <Button
            variant="outline"
            className="flex flex-col items-center p-4"
            onClick={() => {
              onClose();
              // This would trigger the add member modal
              showInfo(
                "Add Member",
                `Add member functionality to ${committee.name} would be implemented here.`
              );
            }}
          >
            <Plus className="h-6 w-6 mb-2" />
            <span className="text-sm">Add Member</span>
          </Button>
        </div>

        {/* Actions */}
        <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
          <Button variant="secondary" onClick={onClose}>
            Close
          </Button>
          <Button
            onClick={() => {
              onClose();
              // This would trigger the edit modal in the parent component
            }}
          >
            <Edit className="h-4 w-4 mr-2" />
            Edit Committee
          </Button>
        </div>
      </div>
    </Modal>
  );
}

// Add Committee Modal Component
function AddCommitteeModal({
  isOpen,
  onClose,
  onAdd,
}: {
  isOpen: boolean;
  onClose: () => void;
  onAdd: (committeeData: Omit<Committee, "id">) => void;
}) {
  const [formData, setFormData] = useState({
    name: "",
    type: "international" as const,
    description: "",
    chairperson: "",
    members: [] as string[],
  });

  const [newMember, setNewMember] = useState("");

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const committeeData = {
      name: formData.name,
      type: formData.type,
      description: formData.description,
      chairperson: formData.chairperson,
      members: formData.members,
      establishedDate: new Date().toISOString().split("T")[0],
      status: "active" as const,
      budget: 0,
      objectives: [],
    };
    onAdd(committeeData);
  };

  const addMember = () => {
    if (newMember.trim()) {
      setFormData({
        ...formData,
        members: [...formData.members, newMember.trim()],
      });
      setNewMember("");
    }
  };

  const removeMember = (index: number) => {
    setFormData({
      ...formData,
      members: formData.members.filter((_, i) => i !== index),
    });
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Add New Committee"
      size="lg"
    >
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Input
            label="Committee Name"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            required
          />
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Committee Type
            </label>
            <select
              value={formData.type}
              onChange={(e) =>
                setFormData({ ...formData, type: e.target.value as any })
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="international">International Relations</option>
              <option value="dispute">Dispute Resolution</option>
              <option value="social">Social</option>
              <option value="media">Media</option>
              <option value="cultural">Cultural</option>
            </select>
          </div>
          <Input
            label="Chairperson"
            value={formData.chairperson}
            onChange={(e) =>
              setFormData({ ...formData, chairperson: e.target.value })
            }
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Description
          </label>
          <textarea
            value={formData.description}
            onChange={(e) =>
              setFormData({ ...formData, description: e.target.value })
            }
            rows={4}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Committee Members
          </label>
          <div className="flex gap-2 mb-3">
            <Input
              value={newMember}
              onChange={(e) => setNewMember(e.target.value)}
              placeholder="Add member name"
              className="flex-1"
            />
            <Button type="button" onClick={addMember}>
              Add
            </Button>
          </div>
          <div className="space-y-2">
            {formData.members.map((member, index) => (
              <div
                key={index}
                className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
              >
                <span className="text-sm text-gray-900">{member}</span>
                <button
                  type="button"
                  onClick={() => removeMember(index)}
                  className="text-red-600 hover:text-red-800"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
        </div>

        <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
          <Button variant="secondary" type="button" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit">Create Committee</Button>
        </div>
      </form>
    </Modal>
  );
}

// Edit Committee Modal Component
function EditCommitteeModal({
  committee,
  isOpen,
  onClose,
  onUpdate,
}: {
  committee: Committee;
  isOpen: boolean;
  onClose: () => void;
  onUpdate: (committee: Committee) => void;
}) {
  const [formData, setFormData] = useState({
    name: committee.name,
    type: committee.type,
    description: committee.description,
    chairperson: committee.chairperson,
    members: committee.members,
    status: committee.status,
    budget: committee.budget || 0,
    objectives: committee.objectives,
  });

  const [newMember, setNewMember] = useState("");
  const [newObjective, setNewObjective] = useState("");

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const updatedCommittee: Committee = {
      ...committee,
      ...formData,
    };
    onUpdate(updatedCommittee);
  };

  const addMember = () => {
    if (newMember.trim()) {
      setFormData({
        ...formData,
        members: [...formData.members, newMember.trim()],
      });
      setNewMember("");
    }
  };

  const removeMember = (index: number) => {
    setFormData({
      ...formData,
      members: formData.members.filter((_, i) => i !== index),
    });
  };

  const addObjective = () => {
    if (newObjective.trim()) {
      setFormData({
        ...formData,
        objectives: [...formData.objectives, newObjective.trim()],
      });
      setNewObjective("");
    }
  };

  const removeObjective = (index: number) => {
    setFormData({
      ...formData,
      objectives: formData.objectives.filter((_, i) => i !== index),
    });
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Edit Committee" size="lg">
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Input
            label="Committee Name"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            required
          />
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Committee Type
            </label>
            <select
              value={formData.type}
              onChange={(e) =>
                setFormData({ ...formData, type: e.target.value as any })
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="international">International Relations</option>
              <option value="dispute">Dispute Resolution</option>
              <option value="social">Social</option>
              <option value="media">Media</option>
              <option value="artistic">Artistic & Cultural</option>
            </select>
          </div>
          <Input
            label="Chairperson"
            value={formData.chairperson}
            onChange={(e) =>
              setFormData({ ...formData, chairperson: e.target.value })
            }
            required
          />
          <Input
            label="Budget"
            type="number"
            value={formData.budget}
            onChange={(e) =>
              setFormData({
                ...formData,
                budget: parseInt(e.target.value) || 0,
              })
            }
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Description
          </label>
          <textarea
            value={formData.description}
            onChange={(e) =>
              setFormData({ ...formData, description: e.target.value })
            }
            rows={4}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Committee Members
          </label>
          <div className="flex gap-2 mb-3">
            <Input
              value={newMember}
              onChange={(e) => setNewMember(e.target.value)}
              placeholder="Add member name"
              className="flex-1"
            />
            <Button type="button" onClick={addMember}>
              Add
            </Button>
          </div>
          <div className="space-y-2">
            {formData.members.map((member, index) => (
              <div
                key={index}
                className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
              >
                <span className="text-sm text-gray-900">{member}</span>
                <button
                  type="button"
                  onClick={() => removeMember(index)}
                  className="text-red-600 hover:text-red-800"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Objectives
          </label>
          <div className="flex gap-2 mb-3">
            <Input
              value={newObjective}
              onChange={(e) => setNewObjective(e.target.value)}
              placeholder="Add objective"
              className="flex-1"
            />
            <Button type="button" onClick={addObjective}>
              Add
            </Button>
          </div>
          <div className="space-y-2">
            {formData.objectives.map((objective, index) => (
              <div
                key={index}
                className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
              >
                <span className="text-sm text-gray-900">{objective}</span>
                <button
                  type="button"
                  onClick={() => removeObjective(index)}
                  className="text-red-600 hover:text-red-800"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
        </div>

        <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
          <Button variant="secondary" type="button" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit">Update Committee</Button>
        </div>
      </form>
    </Modal>
  );
}

// Add Event Modal Component
function AddEventModal({
  isOpen,
  onClose,
  onAdd,
  committeeType,
}: {
  isOpen: boolean;
  onClose: () => void;
  onAdd: (eventData: Omit<Event, "id">) => void;
  committeeType: string;
}) {
  const [formData, setFormData] = useState({
    title: "",
    date: "",
    type: "social" as const,
    location: "",
    attendees: 0,
    budget: 0,
    status: "planned" as const,
    organizer: "",
    description: "",
    maxAttendees: 0,
    registrationDeadline: "",
    committee: committeeType,
    sponsors: [] as string[],
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onAdd(formData);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Add New Event" size="lg">
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Input
            label="Event Title"
            value={formData.title}
            onChange={(e) =>
              setFormData({ ...formData, title: e.target.value })
            }
            required
          />
          <Input
            label="Date"
            type="date"
            value={formData.date}
            onChange={(e) => setFormData({ ...formData, date: e.target.value })}
            required
          />
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Event Type
            </label>
            <select
              value={formData.type}
              onChange={(e) =>
                setFormData({ ...formData, type: e.target.value as any })
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="social">Social</option>
              <option value="cultural">Cultural</option>
              <option value="professional">Professional</option>
              <option value="ceremony">Ceremony</option>
            </select>
          </div>
          <Input
            label="Location"
            value={formData.location}
            onChange={(e) =>
              setFormData({ ...formData, location: e.target.value })
            }
            required
          />
          <Input
            label="Budget"
            type="number"
            value={formData.budget}
            onChange={(e) =>
              setFormData({
                ...formData,
                budget: parseInt(e.target.value) || 0,
              })
            }
          />
          <Input
            label="Max Attendees"
            type="number"
            value={formData.maxAttendees}
            onChange={(e) =>
              setFormData({
                ...formData,
                maxAttendees: parseInt(e.target.value) || 0,
              })
            }
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Description
          </label>
          <textarea
            value={formData.description}
            onChange={(e) =>
              setFormData({ ...formData, description: e.target.value })
            }
            rows={4}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>

        <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
          <Button variant="secondary" type="button" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit">Create Event</Button>
        </div>
      </form>
    </Modal>
  );
}

// Add Partner Modal Component
function AddPartnerModal({
  isOpen,
  onClose,
  onAdd,
}: {
  isOpen: boolean;
  onClose: () => void;
  onAdd: (partnerData: Omit<InternationalPartner, "id">) => void;
}) {
  const [formData, setFormData] = useState({
    name: "",
    type: "ICAO" as const,
    country: "",
    contactPerson: "",
    email: "",
    phone: "",
    relationship: "",
    lastContact: new Date().toISOString().split("T")[0],
    agreements: [] as string[],
    status: "active" as const,
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onAdd(formData);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Add International Partner"
      size="lg"
    >
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Input
            label="Organization Name"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            required
          />
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Partner Type
            </label>
            <select
              value={formData.type}
              onChange={(e) =>
                setFormData({ ...formData, type: e.target.value as any })
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="ICAO">ICAO</option>
              <option value="airline">Airline</option>
              <option value="foreign_syndicate">Foreign Syndicate</option>
              <option value="government">Government</option>
              <option value="other">Other</option>
            </select>
          </div>
          <Input
            label="Country"
            value={formData.country}
            onChange={(e) =>
              setFormData({ ...formData, country: e.target.value })
            }
            required
          />
          <Input
            label="Contact Person"
            value={formData.contactPerson}
            onChange={(e) =>
              setFormData({ ...formData, contactPerson: e.target.value })
            }
            required
          />
          <Input
            label="Email"
            type="email"
            value={formData.email}
            onChange={(e) =>
              setFormData({ ...formData, email: e.target.value })
            }
            required
          />
          <Input
            label="Phone"
            value={formData.phone}
            onChange={(e) =>
              setFormData({ ...formData, phone: e.target.value })
            }
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Relationship Description
          </label>
          <textarea
            value={formData.relationship}
            onChange={(e) =>
              setFormData({ ...formData, relationship: e.target.value })
            }
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>

        <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
          <Button variant="secondary" type="button" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit">Add Partner</Button>
        </div>
      </form>
    </Modal>
  );
}

// Add Dispute Case Modal Component
function AddDisputeCaseModal({
  isOpen,
  onClose,
  onAdd,
}: {
  isOpen: boolean;
  onClose: () => void;
  onAdd: (disputeData: Omit<DisputeCase, "id">) => void;
}) {
  const [formData, setFormData] = useState({
    caseNumber: "",
    title: "",
    complainant: "",
    respondent: "",
    type: "employment" as const,
    status: "submitted" as const,
    priority: "medium" as const,
    assignedMediator: "",
    createdDate: new Date().toISOString().split("T")[0],
    dueDate: "",
    description: "",
    documents: [] as string[],
    mediationSessions: [] as any[],
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onAdd(formData);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Add New Dispute Case"
      size="lg"
    >
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Input
            label="Case Number"
            value={formData.caseNumber}
            onChange={(e) =>
              setFormData({ ...formData, caseNumber: e.target.value })
            }
            required
          />
          <Input
            label="Case Title"
            value={formData.title}
            onChange={(e) =>
              setFormData({ ...formData, title: e.target.value })
            }
            required
          />
          <Input
            label="Complainant"
            value={formData.complainant}
            onChange={(e) =>
              setFormData({ ...formData, complainant: e.target.value })
            }
            required
          />
          <Input
            label="Respondent"
            value={formData.respondent}
            onChange={(e) =>
              setFormData({ ...formData, respondent: e.target.value })
            }
            required
          />
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Case Type
            </label>
            <select
              value={formData.type}
              onChange={(e) =>
                setFormData({ ...formData, type: e.target.value as any })
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="employment">Employment</option>
              <option value="contract">Contract</option>
              <option value="disciplinary">Disciplinary</option>
              <option value="other">Other</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Priority
            </label>
            <select
              value={formData.priority}
              onChange={(e) =>
                setFormData({ ...formData, priority: e.target.value as any })
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
            </select>
          </div>
          <Input
            label="Assigned Mediator"
            value={formData.assignedMediator}
            onChange={(e) =>
              setFormData({ ...formData, assignedMediator: e.target.value })
            }
            required
          />
          <Input
            label="Due Date"
            type="date"
            value={formData.dueDate}
            onChange={(e) =>
              setFormData({ ...formData, dueDate: e.target.value })
            }
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Case Description
          </label>
          <textarea
            value={formData.description}
            onChange={(e) =>
              setFormData({ ...formData, description: e.target.value })
            }
            rows={4}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
            required
          />
        </div>

        <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
          <Button variant="secondary" type="button" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit">Create Case</Button>
        </div>
      </form>
    </Modal>
  );
}
